import React, { useEffect, useMemo, useRef, useState } from 'react';

import { type DynamicFormField } from '@flipdish/api-client-typescript';
import { type Property } from '@flipdish/orgmanagement';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { useMapTools } from 'components/Address/useMapTools';
import { type FormikProps, Form, Formik } from 'formik';
import { debounce } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { getTranslate, Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import PageLayout from '@fd/ui/Layout';

import {
  closeNotifySaving,
  notifyError,
  NotifyProps,
  notifySaved,
  notifySaving,
} from '../../../layouts/Notify/actions';
import GridContainer from '../../../ui/Layout/GridContainer';
import PaperContainer from '../../../ui/Layout/PaperContainer';
import { FormikInputField } from '../../../ui/molecules/FormikInputField';
import AddressSearch from '../../Address/AddressSearch';
import PreventNavigation from '../../Finance/Banking/components/PreventNavigation';
import { dublinCoordinates } from '../constants';
import { usePropertyInitialValues } from '../hooks/usePropertyInitialValues';
import { usePropertyMutations } from '../hooks/usePropertyMutations';
import { validatePropertyForm } from '../utils';
import {
  DisabledTimezoneTypographyStyled,
  TimezoneTypographyStyled,
  useStyles,
} from './CreateAndEditProperty.styles';
import { handleAddressFormChange } from './helpers';
import PropertyAddressForm from './PropertyAddressForm';
import PropertyLocationMap from './PropertyLocationMap';
import PropertyTypeFilter from './PropertyTypeFilter';

type OuterProps = {
  propertyId: string;
  propertyData: Property;
};

type Props = MappedState & MappedDispatch & OuterProps & { canEdit: boolean };

const CreateAndEditProperty = (props: Props) => {
  const {
    accountEmail,
    currentOrg,
    dispatchCloseNotifySaving,
    dispatchNotifyError,
    dispatchNotifySaved,
    dispatchNotifySaving,
    selectedApp,
    translate,
    propertyId,
    propertyData,
    canEdit,
  } = props;
  const [addressFormAndValues, setAddressFormAndValues] = useState<{
    values: Property;
    addressFormData: Array<DynamicFormField>;
  }>({ values: {}, addressFormData: [] });
  const orgId = currentOrg?.orgId || '';

  const mapTools = useMapTools();

  const isEditMode = Boolean(propertyId);
  const classes = useStyles();

  const initialValues = usePropertyInitialValues({
    addressFormAndValues,
    propertyData,
    accountEmail: accountEmail || '',
    orgCountryCode: currentOrg?.countryCode || '',
  });

  const formikRef = useRef<FormikProps<Property> | null>(null);

  const { createPropertyMutation, updatePropertyMutation } = usePropertyMutations({
    orgId,
    propertyId,
    appId: selectedApp?.AppId || '',
    dispatchCloseNotifySaving,
    dispatchNotifyError,
    dispatchNotifySaved,
  });

  const handleSubmit = async (values, { setTouched }) => {
    const { coordinates } = values;
    if (
      coordinates?.latitude === dublinCoordinates.latitude &&
      coordinates?.longitude === dublinCoordinates.longitude
    ) {
      dispatchNotifyError({
        message: 'Make_sure_to_set_your_map_pin_so_customers_can',
        translate: true,
      });
      return;
    }
    dispatchNotifySaving();
    if (isEditMode) {
      await updatePropertyMutation.mutateAsync(values);
    } else {
      await createPropertyMutation.mutateAsync(values);
    }
    setTouched({});
  };

  const [coords, setCoords] = useState<google.maps.LatLngLiteral>({
    lat: initialValues.coordinates.latitude,
    lng: initialValues.coordinates.longitude,
  });
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: initialValues.coordinates.latitude,
    lng: initialValues.coordinates.longitude,
  });
  const [countryCode, setCountryCode] = useState<string>(initialValues.countryCode);

  const [isInSearchMode, setIsInSearchMode] = useState(!isEditMode);

  const handleManualAddressReadyForGeocode = useMemo(() => {
    const debouncedFn = debounce(async (fullAddress: string) => {
      if (!fullAddress) return;
      if (!mapTools?.geoCode) return;

      try {
        const results = await mapTools.geoCode(fullAddress);
        const loc = results?.[0]?.geometry?.location?.toJSON();
        if (!loc) return;

        // update what gets submitted/saved
        applyCoordinatesToFormik(loc as google.maps.LatLngLiteral);

        setCoords(loc);
        setCenter(loc);
      } catch {
        console.error(`Failed to geocode address: ${fullAddress}`);
      }
    }, 800);
    return debouncedFn;
  }, [mapTools]);

  useEffect(() => {
    return () => {
      handleManualAddressReadyForGeocode.cancel();
    };
  }, [handleManualAddressReadyForGeocode]);

  const applyCoordinatesToFormik = (loc: google.maps.LatLngLiteral): void => {
    formikRef.current?.setFieldTouched('coordinates.latitude', true, false);
    formikRef.current?.setFieldValue(
      'coordinates',
      { latitude: loc.lat, longitude: loc.lng },
      false
    );
  };

  const content = (
    <PaperContainer>
      <Formik
        innerRef={formikRef}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize={true}
        initialValues={initialValues}
        validate={(values) => validatePropertyForm(values, translate)}
        onSubmit={handleSubmit}
      >
        {({ dirty, isSubmitting, setFieldValue, touched }: FormikProps<any>) => {
          const onAddressSuggestionClicked = async (result: google.maps.places.PlaceResult) => {
            await handleAddressFormChange({
              result,
              // TODO: Check if this value needs to be hardcoded here
              languageCode: 'en',
              setIsInSearchMode,
              setFieldValue,
              setCoords,
              setCenter,
              setCountryCode,
            });
          };

          return (
            <Form>
              <PreventNavigation when={dirty && !isSubmitting && !isEmpty(touched)} />

              {isInSearchMode && (
                <GridContainer
                  direction="row"
                  justifyContent="space-between"
                  className={classes.item}
                >
                  <Grid item xs={12} sm={5} className={classes.gridItem}>
                    <InputLabel htmlFor="name" style={{ transform: 'initial' }}>
                      {translate('Find_Property_On_Map')}
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} sm={7} className={classes.gridItem}>
                    <AddressSearch
                      currentLocation={coords}
                      onAddressSuggestionClicked={onAddressSuggestionClicked}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5} className={classes.gridItem} />
                  <Grid item xs={12} sm={7} className={classes.gridItem}>
                    <Button
                      type="button"
                      variant="text"
                      color="primary"
                      onClick={() => setIsInSearchMode(false)}
                      disabled={!canEdit}
                      data-fd="enter-address-manually"
                    >
                      {translate('Enter_address_manually')}
                    </Button>
                  </Grid>
                </GridContainer>
              )}

              {!isInSearchMode && (
                <div>
                  <GridContainer
                    direction="row"
                    justifyContent="space-between"
                    className={classes.item}
                  >
                    <Grid item xs={12} sm={5} className={classes.gridItem}>
                      <InputLabel htmlFor="name" style={{ transform: 'initial' }}>
                        {translate('Property_name')}
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} sm={7} className={classes.gridItem}>
                      <FormikInputField
                        variant="standard"
                        fdKey="name"
                        placeholder={translate('Enter_property_name') as string}
                        label={translate('Enter_property_name') as string}
                        name="name"
                        disabled={!canEdit}
                        data-fd="enter-property-name"
                      />
                    </Grid>
                  </GridContainer>
                  <Divider />
                  <PropertyAddressForm
                    setAddressFormAndValues={setAddressFormAndValues}
                    countryCode={countryCode}
                    onCountryChanged={setCountryCode}
                    onManualAddressReadyForGeocode={handleManualAddressReadyForGeocode}
                    isEditMode={isEditMode}
                  />

                  {!isInSearchMode && (
                    <GridContainer
                      direction="row"
                      justifyContent="space-between"
                      className={classes.item}
                    >
                      <Grid item xs={12} sm={5} className={classes.gridItem} />
                      <Grid item xs={12} sm={7} className={classes.gridItem}>
                        <Button
                          type="button"
                          variant="text"
                          color="primary"
                          onClick={() => setIsInSearchMode(true)}
                          disabled={!canEdit}
                        >
                          <Translate id="Return_to_search" />
                        </Button>
                      </Grid>
                    </GridContainer>
                  )}

                  <Divider />
                  <GridContainer
                    direction="row"
                    justifyContent="space-between"
                    className={classes.item}
                  >
                    <Grid item xs={5} className={classes.gridItem}>
                      <Typography variant={'body1'} className={classes.grey}>
                        <Translate id="Confirm_Property_Location" />
                      </Typography>
                    </Grid>
                    <Grid item xs={7} className={clsx(classes.alignSelfCenter, classes.gridItem)}>
                      <Typography variant={'caption'}>
                        <Translate id="Property_Map_Help_Text" />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} className={clsx(classes.mapItem, classes.gridItem)}>
                      <PropertyLocationMap
                        coords={coords}
                        center={center}
                        disabled={!canEdit}
                        updateCoordinatesAction={(coordinates) => {
                          applyCoordinatesToFormik(coordinates);
                        }}
                      />
                    </Grid>
                  </GridContainer>
                  <Divider />
                  <GridContainer
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className={classes.item}
                  >
                    <Grid item xs={12} sm={5} className={classes.gridItem}>
                      <InputLabel htmlFor="phoneNumber" style={{ transform: 'initial' }}>
                        <Translate id="Property_phone" />
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} sm={7} className={classes.gridItem}>
                      <FormikInputField
                        variant="standard"
                        fdKey="phoneNumber"
                        placeholder="+123 45 678-9012"
                        label={translate('Property_phone') as string}
                        name="phoneNumber"
                        disabled={!canEdit}
                      />
                    </Grid>
                  </GridContainer>
                  <Divider />

                  {isEditMode && (
                    <>
                      <GridContainer
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        className={classes.item}
                      >
                        <Grid item xs={12} sm={5} className={classes.gridItem}>
                          <InputLabel>
                            <Translate id="Property_timezone" />
                          </InputLabel>
                        </Grid>
                        <Grid item xs={12} sm={7} className={classes.gridItem}>
                          {canEdit ? (
                            <TimezoneTypographyStyled className={classes.textField}>
                              {propertyData?.timeZone}
                            </TimezoneTypographyStyled>
                          ) : (
                            <DisabledTimezoneTypographyStyled className={classes.textField}>
                              {propertyData?.timeZone}
                            </DisabledTimezoneTypographyStyled>
                          )}
                        </Grid>
                      </GridContainer>
                      <Divider />
                    </>
                  )}
                  <GridContainer
                    direction="row"
                    justifyContent="space-between"
                    className={classes.item}
                  >
                    <Grid item xs={12} sm={5} className={classes.gridItem}>
                      <InputLabel htmlFor="emailAddress" style={{ transform: 'initial' }}>
                        <Translate id="Property_email" />
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} sm={7} className={classes.gridItem}>
                      <FormikInputField
                        variant="standard"
                        fdKey="emailAddress"
                        placeholder={
                          initialValues.emailAddress
                            ? initialValues.emailAddress
                            : (translate('Add_email') as string)
                        }
                        label={translate('Add_email') as string}
                        name="emailAddress"
                        disabled={!canEdit}
                      />
                    </Grid>
                  </GridContainer>
                  <Divider />
                  <GridContainer
                    direction="row"
                    justifyContent="space-between"
                    className={classes.item}
                  >
                    <Grid item xs={12} sm={5} className={classes.gridItem}>
                      <InputLabel htmlFor="propertyType" style={{ transform: 'initial' }}>
                        <Translate id="Property_type" />
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} sm={7} className={classes.gridItem}>
                      <PropertyTypeFilter
                        initialType={initialValues.type}
                        onChange={async (value) => {
                          await setFieldValue('type', value);
                        }}
                        disabled={!canEdit}
                      />
                    </Grid>
                  </GridContainer>
                  <Divider />
                  <Grid container className={classes.gridItem} justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={!canEdit}
                      data-fd="submit-button"
                    >
                      {isEditMode ? translate('Save') : translate('Add_Property')}
                    </Button>
                  </Grid>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </PaperContainer>
  );

  return isEditMode ? (
    content
  ) : (
    <PageLayout title={translate('Add_new_property')}>{content}</PageLayout>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  return {
    selectedApp: state.currentApp,
    currentOrg: state.rms.currentOrg,
    translate: getTranslate(state.locale),
    accountEmail: state.account.Email,
    canEdit: state.permissions?.includes('EditStores'),
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  dispatchCloseNotifySaving: () => dispatch(closeNotifySaving()),
  dispatchNotifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  dispatchNotifySaved: () => dispatch(notifySaved()),
  dispatchNotifySaving: () => dispatch(notifySaving({ persist: true })),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateAndEditProperty);
